import React, { useEffect, useState, useMemo, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import TagIcon from '@mui/icons-material/Tag';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { QRCodeSVG } from 'qrcode.react';
import RoomService from '../services/RoomService';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import FullscreenExitSharpIcon from '@mui/icons-material/FullscreenExitSharp';
import Chat from '../components/chat/Chat';
import PeopleDrawer from '../components/presentation-components/PeopleDrawer';
import RoomSettingsDrawer from '../components/presentation-components/RoomSettingsDrawer';
import LeftPaneSection from '../components/presentation-components/LeftPaneSection';
import PresentationDrawer from '../components/presentation-components/PresentationDrawer';
import TextFadeOut from '../components/presentation-components/TextFadeOut';
import Vyur from '../components/presentation-components/Vyur';
import RoomLayout from '../components/presentation-components/RoomLayout';
import ImageService from '../services/ImageService';
import EventManager from '../Util/EventManager';
import MessageContext from '../App/Contexts/MessageContext';
import UserContext from '../App/Contexts/UserContext';
import AppHeader from '../components/AppHeader/AppHeader';
import QRCodeModal from '../components/presentation-components/QRCodeModal';
import PresentationModeInfo, { PRESENTATION_MODE_TOOLTIP } from '../components/info-button/PresentationModeInfo';
import useFullscreen from '../Util/custom-hooks/useFullscreen';
import useSlides from '../Util/custom-hooks/useSlides';
import useFileUploads from '../components/presentation-components/useFileUploads';
import useSlideKeyboardNav from '../Util/custom-hooks/useSlideKeyboardNav';

// Accent shared by the header switch knob and the slide-panel border so the
// admin gets one unmistakable "Presentation Mode is live" signal.
const presentationModeAccent = '#C724B1';

export default function PresentationRoom() {
    const navigate = useNavigate();
    const { setMessage } = useContext(MessageContext);
    const user = useContext(UserContext);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const [thisRoom, setThisRoom] = useState({});
    const { fullscreen, toggleFullscreen } = useFullscreen();

    // Todo: keep cursor in a db and set it on demand
    const [cursor, setCursor] = useState('url(/images/orange-arrow.png) 1 1, auto');
    const [roommateIds, setroommateIds] = useState([]);
    const [clicker, setClicker] = useState();

    const {
        setItems,
        slidesMap,
        currentImg,
        updateImage,
        getSlides,
        loadingSlides,
        sortAsc,
        toggleSort,
        maxIndex,
        selectSlideLocally,
        getPresentationLoadedProgress
    } = useSlides((e) => setMessage(e.message));

    const loadImageBlob = (roomId) => (item) => ImageService.getImageBlob(roomId, item.itemName);

    function initRoom() {
        const url = /presentation\/(\w+)/g.exec(window.location.pathname)?.[1];
        RoomService.roomLogin(url)
            .then((room) => {
                setThisRoom(room);
                const idsOfOtherUsersForThisRoom = room.roommates
                    .filter((mate) => mate.id.userId != user.userId)
                    .map((mate) => mate.id.userId);
                setroommateIds(idsOfOtherUsersForThisRoom);
                setItems(room.presentationItems, loadImageBlob(room.roomId));
            })
            .catch((e) => {
                console.log(e);
                if (e.message.includes('not found')) {
                    setMessage('Room not found');
                    navigate(`/home`);
                } else {
                    setMessage(e.message);
                }
            });
    }

    useEffect(() => {
        initRoom();
    }, []);

    useEffect(() => {
        // Read the id off the room rather than destructuring it: destructuring
        // makes this effect depend on the whole object, which is the churn
        // this is avoiding.
        const roomId = thisRoom.roomId;
        if (roomId && user.userId) {
            RoomService.heartbeat(roomId, user.userId); // send one now, then send one every so often until leaving the room
            const interval = setInterval(() => RoomService.heartbeat(roomId, user.userId), 20000); // 20 seconds

            EventManager.reactConnectEvent((data) => {
                if (
                    data.sender.firstName &&
                    data.payload.roomId === roomId &&
                    data.sender.userId !== user.userId
                ) {
                    setMessage(`${data.sender.firstName} has ${data.eventType === 'USER_CONNECT' ? 'joined' : 'left'} the room`);
                }
                refreshRoom();
            });

            return () => {
                clearInterval(interval);
                EventManager.unsetConnectEvent();
            };
        }
        // Depend on the room id, not the room object. `thisRoom` is replaced
        // wholesale by every refreshRoom(), so depending on it would tear down
        // and rebuild the interval and the connect listener each time anyone
        // toggles a room setting - restarting the 20s beat before it ever
        // fires, and dropping join/leave notices in the gap. Nothing in here
        // reads the room beyond its id: refreshRoom() derives the url from the
        // location, so an older render's copy behaves identically.
    }, [thisRoom.roomId, user.userId]);

    const refreshPresentation = () => {
        const url = /presentation\/(\w+)/g.exec(window.location.pathname)?.[1];
        return RoomService.getPresentationForRoomUrl(url)
            .then(({ presentationItems, roomId }) => setItems(presentationItems, loadImageBlob(roomId)))
            .catch((e) => {
                console.log(e);
                setMessage(e.message);
            });
    };

    // Re-fetch the full room (settings + roommates + presentation). Shared by the
    // connect/disconnect event handler above and room-level changes such as
    // feature toggles / Presentation Mode so both stay in sync via one code path.
    const refreshRoom = () => {
        const url = /presentation\/(\w+)/g.exec(window.location.pathname)?.[1];
        return RoomService.getRoomByRoomUrl(url)
            .then((room) => {
                setThisRoom(room);
                const idsOfOtherUsersForThisRoom = (room.roommates || [])
                    .filter((mate) => mate.id.userId !== user.userId)
                    .map((mate) => mate.id.userId);
                setroommateIds(idsOfOtherUsersForThisRoom);
                setItems(room.presentationItems, loadImageBlob(room.roomId));
            })
            .catch((e) => {
                console.log(e);
                setMessage(e.message);
            });
    };

    const updateRoom = (event) => {
        if (event.eventType == 'INDEX_CHANGE') {
            setClicker(`${event.sender.firstName} ${event.sender.lastName}`);
            refreshPresentation();
            return;
        } else if (event.eventType === 'DELETE' && event.payload.roomId === thisRoom.roomId) {
            setMessage('Room was deleted by the owner: ' + event.payload.name);
            navigate(`/home`);
            return;
        }
        // UPDATE / CREATE / PRESENTATION_CHANGE: refresh the whole room so feature
        // settings flip in real time, not just the presentation items.
        refreshRoom();
    };

    useEffect(() => {
        EventManager.reactRoomEvent(updateRoom);
        return () => {
            EventManager.unsetRoomEvent();
        };
        // Same reasoning as the heartbeat effect above: updateRoom reads
        // nothing off the room but its id, so re-registering the handler on
        // every room-object replacement is churn. Re-register when the room
        // the page is showing actually changes.
    }, [thisRoom.roomId, user.userId]);

    const isAdmin = (thisRoom.roommates || []).some(
        (r) => r.role === 'ADMIN' && r.id.userId === user.userId
    ) || thisRoom.creator?.userId === user.userId;

    // Room admins, for the chat panel's admin-coloured avatars.
    const adminUserIds = (thisRoom.roommates || [])
        .filter((r) => r.role === 'ADMIN')
        .map((r) => r.id.userId);

    // Room feature settings. The per-feature flags record which features stay
    // available to the audience while Presentation Mode is on; they have no
    // effect while it is off. Missing values fall back to the backend defaults
    // (drawing and Image Q locked during presentations, chat and reactions on).
    const roomSettings = {
        drawingEnabled: thisRoom.drawingEnabled === true,
        imageQEnabled: thisRoom.imageQEnabled === true,
        chatEnabled: thisRoom.chatEnabled !== false,
        reactionsEnabled: thisRoom.reactionsEnabled !== false,
        presentationMode: !!thisRoom.presentationMode
    };

    // The current user's interactivity flag (on by default). While on, every
    // feature works; while off, the room's feature settings decide, as if
    // Presentation Mode were on for just this user. Presentation Mode itself
    // doesn't gate features - flipping it bulk-resets these flags instead.
    const myRoomUser = (thisRoom.roommates || []).find((r) => r.id.userId === user.userId);
    const myInteractive = myRoomUser ? myRoomUser.interactive !== false : true;

    // Effective per-feature permissions for the current user. Admins always
    // retain access.
    const canDraw = isAdmin || myInteractive || roomSettings.drawingEnabled;
    const canUseImageQ = isAdmin || myInteractive || roomSettings.imageQEnabled;
    const canChat = isAdmin || myInteractive || roomSettings.chatEnabled;
    const canReact = isAdmin || myInteractive || roomSettings.reactionsEnabled;

    // Persist a settings change and apply it optimistically so the admin's own UI
    // reacts instantly; the backend echoes it to every other user via SSE.
    const handleSettingsChange = (next) => {
        setThisRoom((prev) => ({ ...prev, ...next }));
        RoomService.updateRoomSettings({ roomId: thisRoom.roomId, ...next }).catch((e) => {
            setMessage(e.message);
            refreshRoom();
        });
    };

    // Per-user interactivity: optimistic local update, then persist; the backend
    // echoes an UPDATE event so every other client reconciles.
    const handleToggleUserInteractivity = (userId, interactive) => {
        setThisRoom((prev) => ({
            ...prev,
            roommates: (prev.roommates || []).map((r) =>
                r.id.userId === userId ? { ...r, interactive } : r
            )
        }));
        RoomService.setUserInteractivity(thisRoom.roomId, userId, interactive).catch((e) => {
            setMessage(e.message);
            refreshRoom();
        });
    };

    // Header toggle: flip the interactivity override for all non-admin roommates
    // at once (used when any/none of the individual overrides are on).
    const handleToggleAllInteractivity = (interactive) => {
        setThisRoom((prev) => ({
            ...prev,
            roommates: (prev.roommates || []).map((r) =>
                r.role !== 'ADMIN' ? { ...r, interactive } : r
            )
        }));
        RoomService.setAllInteractivity(thisRoom.roomId, interactive).catch((e) => {
            setMessage(e.message);
            refreshRoom();
        });
    };

    // Presentation Mode: flipping it bulk-resets every non-admin's interactive
    // flag (off when it turns on, back on when it turns off); the backend does
    // the same, so mirror it optimistically here. The per-feature settings are
    // left untouched - they persist as the lockdown mask for non-interactive
    // users. Shared by the Settings panel toggle and the prominent header control.
    const applyPresentationMode = (value) => {
        setThisRoom((prev) => ({
            ...prev,
            roommates: (prev.roommates || []).map((r) =>
                r.role !== 'ADMIN' ? { ...r, interactive: !value } : r
            )
        }));
        handleSettingsChange({ ...roomSettings, presentationMode: value });
    };

    // Admin-only visual cue that Presentation Mode is live: the header switch
    // knob and the slide panel border both light up in the accent color.
    const showPresentationAccent = isAdmin && roomSettings.presentationMode;

    const currentSlideItemId = useMemo(
        () => Object.values(slidesMap).find((s) => s.selected)?.itemId,
        [slidesMap]
    );

    const _sortedSlides = getSlides();
    const _selectedIdx = _sortedSlides.findIndex((s) => s.selected);
    const hasPrevSlide = _selectedIdx > 0 && !!_sortedSlides[_selectedIdx - 1]?.burl;
    const hasNextSlide = _selectedIdx !== -1 && _selectedIdx < _sortedSlides.length - 1 && !!_sortedSlides[_selectedIdx + 1]?.burl;

    const goToSlide = (slide) => {
        selectSlideLocally(slide.index);
        const img = document.createElement('img');
        img.src = slide.burl;
        RoomService.setActiveImageInPresentation(thisRoom.roomId, slide.itemId)
            .catch((e) => setMessage(e.message));
        updateImage(img);
    };

    const prevSlide = () => {
        const sorted = getSlides();
        const idx = sorted.findIndex((s) => s.selected);
        if (idx > 0 && sorted[idx - 1].burl) {
            goToSlide(sorted[idx - 1]);
        }
    };

    const nextSlide = () => {
        const sorted = getSlides();
        const idx = sorted.findIndex((s) => s.selected);
        if (idx !== -1 && idx < sorted.length - 1 && sorted[idx + 1].burl) {
            goToSlide(sorted[idx + 1]);
        }
    };

    // Arrow keys and Space change the slide, for the same people the on-slide
    // prev/next arrows are shown to: the room's admins.
    useSlideKeyboardNav({ onPrev: prevSlide, onNext: nextSlide, enabled: isAdmin });

    const renderVyur = ({ fullscreen: isFullscreen }) => (
        <Vyur
            cursor={cursor}
            img={currentImg}
            roommateIds={roommateIds}
            roomId={thisRoom.roomId}
            fullscreen={isFullscreen}
            isAdmin={isAdmin}
            onPrevSlide={prevSlide}
            onNextSlide={nextSlide}
            hasPrevSlide={hasPrevSlide}
            hasNextSlide={hasNextSlide}
            slideId={currentSlideItemId}
            readOnly={!canDraw}
            showReactions={canReact}
            hudActions={[
                {
                    text: 'Full Screen',
                    icon: fullscreen ? <FullscreenExitSharpIcon /> : <FullscreenRoundedIcon />,
                    onClick: toggleFullscreen
                }
            ]}
        />
    );

    // The slide drawer's compose buttons ask the chat panel to tag the next
    // message to that slide and take the caret. A counter rather than a flag,
    // so pressing the same slide's button twice is two requests - see Chat,
    // which hands the request back once it has applied it.
    const [composeRequest, setComposeRequest] = useState(null);
    const composeKeyRef = useRef(0);
    const requestCompose = (slide, question) => {
        composeKeyRef.current += 1;
        setComposeRequest({ slideId: slide.itemId, question, key: composeKeyRef.current });
    };

    // Here rather than in the drawer: the mobile Slides tab unmounts it, and an
    // upload's tile has to be there when the user comes back.
    const fileUploads = useFileUploads({
        roomId: thisRoom.roomId,
        maxIndex,
        onUpload: refreshPresentation,
        slides: _sortedSlides,
        loadingSlides
    });

    const renderPresentationDrawer = (horizontal) => (
        <PresentationDrawer
            imgClick={updateImage}
            onSort={toggleSort}
            slides={getSlides()}
            loadingSlides={loadingSlides}
            uploads={fileUploads}
            sortAsc={sortAsc}
            roomId={thisRoom.roomId}
            onDelete={(slide) => {
                ImageService.deleteImage(thisRoom.roomId, slide.itemName)
                    .then(updateRoom)
                    .catch((e) => setMessage(e.message));
            }}
            adminIds={(thisRoom.roommates || [])
                .filter((r) => r.role === 'ADMIN')
                .map((r) => r.id.userId)}
            roomOwnerId={thisRoom.creator?.userId}
            horizontal={horizontal}
            canChat={canChat}
            onCompose={requestCompose}
            {...getPresentationLoadedProgress()}
        />
    );

    const peoplePanel = (
        <PeopleDrawer
            roomData={thisRoom}
            isAdmin={isAdmin}
            onToggleUser={handleToggleUserInteractivity}
            onToggleAll={handleToggleAllInteractivity}
        />
    );

    // Mobile bottom panel: only show tabs for features that are enabled, so a
    // disabled Chat or Image Q drops its tab entirely (People is always shown).
    const mobileTabs = [];
    if (canChat) {
        mobileTabs.push({
            label: 'Chat',
            content: (
                <Chat
                    roomId={thisRoom ? thisRoom.roomId : null}
                    adminUserIds={adminUserIds}
                    slides={getSlides()}
                    composeRequest={composeRequest}
                    onComposeHandled={() => setComposeRequest(null)}
                />
            )
        });
    }
    mobileTabs.push({
        label: 'People',
        content: <Box sx={{ p: 2 }}>{peoplePanel}</Box>
    });
    if (canUseImageQ) {
        mobileTabs.push({
            label: 'Slides',
            content: renderPresentationDrawer(true)
        });
    }
    if (isAdmin) {
        // Compact gear-only tab so the strip stays narrow; mirrors the
        // desktop "Settings" accordion.
        mobileTabs.push({
            label: 'Settings',
            icon: <SettingsIcon fontSize="small" />,
            content: (
                <Box sx={{ p: 2 }}>
                    <RoomSettingsDrawer
                        settings={roomSettings}
                        onChange={handleSettingsChange}
                        onTogglePresentationMode={applyPresentationMode}
                    />
                </Box>
            )
        });
    }

    const header = (
        <AppHeader
            roomName={thisRoom.name}
            roomAction={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isAdmin && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip title={PRESENTATION_MODE_TOOLTIP}>
                                <FormControlLabel
                                    sx={{ m: 0, color: 'inherit' }}
                                    control={
                                        <Switch
                                            size="small"
                                            color="default"
                                            checked={roomSettings.presentationMode}
                                            inputProps={{ 'aria-label': 'Presentation Mode' }}
                                            onChange={(e) => applyPresentationMode(e.target.checked)}
                                            sx={{
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                                    opacity: 1
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                    opacity: 1
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                                                    backgroundColor: presentationModeAccent
                                                }
                                            }}
                                        />
                                    }
                                    labelPlacement="start"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="body2" sx={{ color: 'inherit', display: { xs: 'none', sm: 'block' } }}>
                                                Presentation Mode
                                            </Typography>
                                            <PresentationModeInfo sx={{ color: 'inherit', mx: 0.25 }} />
                                        </Box>
                                    }
                                />
                            </Tooltip>
                        </Box>
                    )}
                    {isSmallScreen ? (
                        <QRCodeModal url={window.location.href} entryCode={thisRoom.entryCode}>
                            <Chip
                                icon={<QrCodeIcon />}
                                label={thisRoom.entryCode || 'N/A'}
                                variant="outlined"
                                size="small"
                                clickable
                                sx={{ color: 'inherit', borderColor: 'currentColor', '& .MuiChip-icon': { color: 'inherit' } }}
                            />
                        </QRCodeModal>
                    ) : null}
                </Box>
            }
        />
    );

    const leftPane = (
        <>
            {/* People anchored to the top */}
            <LeftPaneSection title="People" id="people-panel-header">
                {peoplePanel}
            </LeftPaneSection>

            {/* Flexible gap: all extra space collects between People and Settings */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Settings + Room ID anchored to the bottom */}
            {isAdmin && (
                <LeftPaneSection title="Settings" id="settings-panel-header" defaultExpanded={false}>
                    <RoomSettingsDrawer
                        settings={roomSettings}
                        onChange={handleSettingsChange}
                        onTogglePresentationMode={applyPresentationMode}
                    />
                </LeftPaneSection>
            )}

            <LeftPaneSection title="Room ID" id="roomid-panel-header">
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <QRCodeModal url={window.location.href} entryCode={thisRoom.entryCode}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                            <QRCodeSVG value={window.location.href} size={140} />
                            <Chip
                                icon={<TagIcon />}
                                label={thisRoom.entryCode || 'N/A'}
                                variant="outlined"
                                size="small"
                            />
                        </Box>
                    </QRCodeModal>
                </Box>
            </LeftPaneSection>
        </>
    );

    return (
        <RoomLayout
            loading={!thisRoom?.name}
            fullscreen={fullscreen}
            renderVyur={renderVyur}
            vyurOverlay={<TextFadeOut text={clicker} />}
            accentColor={showPresentationAccent ? presentationModeAccent : null}
            header={header}
            leftPane={leftPane}
            chat={
                canChat ? (
                    <Chat
                        roomId={thisRoom ? thisRoom.roomId : null}
                        adminUserIds={adminUserIds}
                        slides={getSlides()}
                        composeRequest={composeRequest}
                        onComposeHandled={() => setComposeRequest(null)}
                    />
                ) : null
            }
            rightPane={canUseImageQ ? renderPresentationDrawer(false) : null}
            mobileTabs={mobileTabs}
            // On mobile the drawer and the chat are separate tabs, so a compose
            // request has to bring the Chat tab forward - otherwise the caret
            // lands somewhere the user can't see.
            activateMobileTab={
                composeRequest ? { label: 'Chat', key: composeRequest.key } : null
            }
        />
    );
}
